import React, { Component } from "react";
import Header from "./layout/Header";
import Footer from "./layout/Footer";
import { Spin, message, Modal } from "antd";
import Bridge from "./utils/Bridge";
import api from "./api/api";
import URLs from "./utils/Urls";
import { logEvent } from "./utils/analytics";

const ALLOWED_UPLOAD_EXTENSIONS = [
  "pdf",
  "jpg",
  "jpeg",
  "png",
  "gif",
  "bmp",
  "webp",
  "docx",
];
const ALLOWED_UPLOAD_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/bmp",
  "image/webp",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const ALLOWED_UPLOAD_ACCEPT =
  ".pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.docx,image/*,application/pdf";

export default class UploadPDFForGeneticConfirmation extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      documents: [],
      selectedFiles: [],
      renamingDocumentId: null,
      renameValue: "",
      viewingDocument: null,
      documentUrl: null,
      documentType: null,
    };
    this.fileInputRef = React.createRef();
  }

  componentDidMount() {
    this.check_genetic_confirmation_documents();
  }

  check_genetic_confirmation_documents = () => {
    let params = "?eals_id=" + localStorage.getItem("ap_eals_id");
    this.setState({ loading: true });
    Bridge.genetic_confirmation_document
      .get(params)
      .then((response) => {
        if (response.status == "1") {
          const documents = (response.data || []).sort(
            (a, b) =>
              (b.genetic_confirmation_document_id || 0) -
              (a.genetic_confirmation_document_id || 0)
          );
          this.setState({
            documents,
            loading: false,
          });
        } else {
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  };

  upload_files = () => {
    if (this.state.selectedFiles.length === 0) {
      message.warning("Please select at least one file before uploading.");
      return;
    }

    const invalidFiles = this.state.selectedFiles.filter(
      (file) => !this.isAllowedUploadFile(file)
    );
    if (invalidFiles.length > 0) {
      message.warning(
        "Please select only PDF, image (JPEG, PNG, GIF, BMP, WebP), or DOCX files."
      );
      return;
    }

    var formData = new FormData();
    this.state.selectedFiles.forEach((file) => {
      formData.append("files", file);
    });
    formData.append("eals_id", localStorage.getItem("ap_eals_id"));

    this.setState({ loading: true });
    Bridge.genetic_confirmation_document
      .upload(formData)
      .then((response) => {
        if (response.status == "1") {
          logEvent("upload_alsfrs_ecas_pdf_form");
          message.success(response.message);
          if (this.fileInputRef.current) {
            this.fileInputRef.current.value = "";
          }
          this.setState(
            {
              selectedFiles: [],
              loading: false,
            },
            () => this.check_genetic_confirmation_documents()
          );
        } else {
          message.warning(response.message || "Upload failed.");
          this.setState({ loading: false });
        }
      })
      .catch((error) => {
        message.warning(
          error?.response?.data?.message ||
            "Upload failed. Please try again."
        );
        this.setState({ loading: false });
      });
  };

  getDocumentId = (doc) => doc.genetic_confirmation_document_id;

  getDisplayName = (doc, index) => {
    let displayName = doc.document_name || `Document ${index + 1}`;
    if (doc.document && !displayName.includes(".")) {
      const fileName = doc.document.split("/").pop();
      if (fileName && fileName.includes(".")) {
        displayName = fileName;
      }
    }
    return displayName;
  };

  isAllowedUploadFile = (file) => {
    const extension = (file.name || "").split(".").pop().toLowerCase();
    if (ALLOWED_UPLOAD_EXTENSIONS.includes(extension)) {
      return true;
    }
    return ALLOWED_UPLOAD_MIME_TYPES.includes(file.type);
  };

  isDownloadOnlyDocument = (displayName, doc) => {
    const name = (displayName || doc?.document_name || doc?.document || "")
      .split("?")[0]
      .toLowerCase();
    return (
      name.endsWith(".pdf") ||
      name.endsWith(".doc") ||
      name.endsWith(".docx")
    );
  };

  download_pdf = (documentId, displayName) => {
    this.setState({ loading: true });
    let params =
      "?eals_id=" +
      localStorage.getItem("ap_eals_id") +
      "&document_id=" +
      documentId;

    api
      .get(URLs.view_genetic_confirmation_document + params, {
        responseType: "blob",
      })
      .then((res) => {
        const contentType = (res.headers["content-type"] || "").toLowerCase();

        const triggerDownload = (blob, fileName) => {
          const blobUrl = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = fileName || "document.pdf";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
          logEvent("pdf_downloaded");
          this.setState({ loading: false });
        };

        if (contentType.includes("application/json")) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const jsonResponse = JSON.parse(reader.result);
              if (jsonResponse.status == "1" && jsonResponse.url) {
                const link = document.createElement("a");
                link.href = jsonResponse.url;
                link.download = displayName || "document.pdf";
                link.target = "_blank";
                link.rel = "noopener noreferrer";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                logEvent("pdf_downloaded");
                this.setState({ loading: false });
              } else {
                message.warning(
                  jsonResponse.message || "Failed to download document"
                );
                this.setState({ loading: false });
              }
            } catch (e) {
              message.warning("Failed to download document");
              this.setState({ loading: false });
            }
          };
          reader.readAsText(res.data);
          return;
        }

        const pdfBlob =
          res.data instanceof Blob
            ? new Blob([res.data], {
                type: res.data.type || "application/octet-stream",
              })
            : new Blob([res.data], { type: "application/octet-stream" });
        triggerDownload(pdfBlob, displayName || "document");
      })
      .catch(() => {
        message.warning("Failed to download document");
        this.setState({ loading: false });
      });
  };

  view_pdf = (documentId, displayName) => {
    // PDFs use download instead of in-modal view
    if (this.isDownloadOnlyDocument(displayName)) {
      this.download_pdf(documentId, displayName);
      return;
    }

    this.setState({ loading: true });
    let params =
      "?eals_id=" +
      localStorage.getItem("ap_eals_id") +
      "&document_id=" +
      documentId;

    api
      .get(URLs.view_genetic_confirmation_document + params, {
        responseType: "blob",
      })
      .then((res) => {
        const contentType = res.headers["content-type"] || "";

        if (contentType.includes("application/json")) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const jsonResponse = JSON.parse(reader.result);
              if (jsonResponse.status == "1" && jsonResponse.url) {
                logEvent("pdf_viewed");
                const fileExtension = (displayName || jsonResponse.url)
                  .split(".")
                  .pop()
                  .split("?")[0]
                  .toLowerCase();
                const isImage = [
                  "jpg",
                  "jpeg",
                  "png",
                  "gif",
                  "bmp",
                  "webp",
                ].includes(fileExtension);

                this.setState({
                  loading: false,
                  viewingDocument: documentId,
                  documentUrl: jsonResponse.url,
                  documentType: isImage ? "image" : "document",
                });
              } else {
                message.warning(
                  jsonResponse.message || "Failed to load document"
                );
                this.setState({ loading: false });
              }
            } catch (e) {
              message.warning("Failed to load document");
              this.setState({ loading: false });
            }
          };
          reader.readAsText(res.data);
          return;
        }

        logEvent("pdf_viewed");
        const blobUrl = URL.createObjectURL(res.data);
        this.setState({
          loading: false,
          viewingDocument: documentId,
          documentUrl: blobUrl,
          documentType: "document",
        });
      })
      .catch(() => {
        message.warning("Failed to load document");
        this.setState({ loading: false });
      });
  };

  closeDocumentViewer = () => {
    if (this.state.documentUrl && this.state.documentUrl.startsWith("blob:")) {
      URL.revokeObjectURL(this.state.documentUrl);
    }
    this.setState({
      viewingDocument: null,
      documentUrl: null,
      documentType: null,
    });
  };

  startRename = (documentId, currentName) => {
    const nameWithoutExt = currentName.includes(".")
      ? currentName.substring(0, currentName.lastIndexOf("."))
      : currentName;
    this.setState({
      renamingDocumentId: documentId,
      renameValue: nameWithoutExt,
    });
  };

  cancelRename = () => {
    this.setState({
      renamingDocumentId: null,
      renameValue: "",
    });
  };

  saveRename = (documentId, currentName) => {
    const newName = this.state.renameValue.trim();
    if (!newName) {
      message.warning("Document name cannot be empty.");
      return;
    }

    const fileExtension = currentName.includes(".")
      ? currentName.substring(currentName.lastIndexOf("."))
      : "";
    const newNameWithExt = newName + fileExtension;

    if (newNameWithExt === currentName) {
      this.cancelRename();
      return;
    }

    this.setState({ loading: true });
    const data = {
      eals_id: localStorage.getItem("ap_eals_id"),
      document_id: documentId,
      document_name: newNameWithExt,
    };

    Bridge.genetic_confirmation_document
      .rename(data)
      .then((response) => {
        if (response.status == "1") {
          message.success(response.message);
          this.setState(
            {
              loading: false,
              renamingDocumentId: null,
              renameValue: "",
            },
            () => this.check_genetic_confirmation_documents()
          );
        } else {
          message.warning(response.message || "Failed to rename document");
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        message.warning("Failed to rename document");
        this.setState({ loading: false });
      });
  };

  remove_pdf = (documentId) => {
    Modal.confirm({
      title: "Confirm Removal",
      content:
        "Are you sure you want to remove this document? This action cannot be undone.",
      okText: "Yes, Remove",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        this.setState({ loading: true });
        let params =
          "?eals_id=" +
          localStorage.getItem("ap_eals_id") +
          "&document_id=" +
          documentId;
        Bridge.genetic_confirmation_document
          .remove_pdf(params)
          .then((response) => {
            if (response.status == "1") {
              logEvent("pdf_removed");
              message.success(response.message);
              this.setState(
                {
                  loading: false,
                },
                () => this.check_genetic_confirmation_documents()
              );
            } else {
              message.warning(response.message);
              this.setState({ loading: false });
            }
          })
          .catch(() => {
            this.setState({ loading: false });
          });
      },
    });
  };

  onChangeFiles = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter((file) => this.isAllowedUploadFile(file));
    const invalidCount = files.length - validFiles.length;

    if (invalidCount > 0) {
      message.warning(
        "Only PDF, image (JPEG, PNG, GIF, BMP, WebP), and DOCX files are allowed."
      );
      if (this.fileInputRef.current) {
        this.fileInputRef.current.value = "";
      }
    }

    this.setState({
      selectedFiles: validFiles,
    });
  };

  render() {
    const inputFileStyles = {
      padding: "9px 9px",
    };
    const documentItemStyles = {
      border: "1px solid #ddd",
      borderRadius: "8px",
      padding: "15px",
      marginBottom: "10px",
      backgroundColor: "#f9f9f9",
    };
    const documentNameStyles = {
      fontWeight: "bold",
      marginBottom: "5px",
    };
    const buttonGroupStyles = {
      display: "flex",
      gap: "10px",
      marginTop: "10px",
      flexWrap: "wrap",
    };
    const documentActionButtonStyles = {
      fontSize: "12px",
      padding: "8px 15px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      lineHeight: 1,
    };
    const documentActionIconStyles = {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    };
    const uploadButtonStyles = {
      marginTop: "15px",
    };

    return (
      <div>
        <Header />
        <div
          className="main-content demographics-wrapper"
          style={{ paddingTop: 40 }}
        >
          <div className="container" style={{ minHeight: 300 }}>
            <div className="row">
              <div className="col-lg-8">
                <a href="/dashboard" className="dashboard-back-arrow mt-0">
                  <box-icon
                    name="chevron-left"
                    color="#fff"
                    style={{ position: "relative", top: 1 }}
                  ></box-icon>
                  Dashboard
                </a>
                <Spin spinning={this.state.loading}>
                  <div className="change-password-form">
                    <h3 className="pb-0 mb-0">
                      Upload Documents for Genetic Confirmation
                    </h3>
                    <hr />

                    <div className="form-group pt-4">
                      <label>
                        <box-icon
                          type="solid"
                          name="file"
                          color="#9e9e9e"
                          size="23px"
                          style={{ marginTop: 0, marginRight: 12 }}
                        ></box-icon>{" "}
                        Select Files
                      </label>
                      <p style={{ fontSize: "12px", color: "#666", marginBottom: "8px" }}>
                        Allowed: PDF, images (JPEG, PNG, GIF, BMP, WebP), DOCX
                      </p>
                      <input
                        type="file"
                        ref={this.fileInputRef}
                        onChange={this.onChangeFiles}
                        accept={ALLOWED_UPLOAD_ACCEPT}
                        multiple
                        style={inputFileStyles}
                      />
                      {this.state.selectedFiles.length > 0 && (
                        <div style={{ marginTop: "10px" }}>
                          <strong>Selected Files:</strong>
                          <ul style={{ marginTop: "5px" }}>
                            {this.state.selectedFiles.map((file, index) => (
                              <li key={index}>
                                {file.name}
                                {file.type ? (
                                  <span
                                    style={{
                                      fontSize: "12px",
                                      color: "#666",
                                      marginLeft: "10px",
                                    }}
                                  >
                                    ({file.type})
                                  </span>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <button
                        className="submit-button"
                        onClick={() => this.upload_files()}
                        style={uploadButtonStyles}
                      >
                        Upload{" "}
                        {this.state.selectedFiles.length > 0
                          ? `(${this.state.selectedFiles.length} files)`
                          : ""}{" "}
                        <box-icon
                          name="chevron-right"
                          color="#fff"
                          style={{ position: "relative", top: 1 }}
                        ></box-icon>
                      </button>
                    </div>

                    {this.state.documents.length > 0 && (
                      <div style={{ marginTop: "30px" }}>
                        <h4>Uploaded Documents</h4>
                        <hr />
                        {this.state.documents.map((doc, index) => {
                          const displayName = this.getDisplayName(doc, index);
                          const documentId = this.getDocumentId(doc);
                          const isDownloadOnly = this.isDownloadOnlyDocument(
                            displayName,
                            doc
                          );
                          const isRenaming =
                            this.state.renamingDocumentId === documentId;

                          return (
                            <div
                              key={documentId || index}
                              style={documentItemStyles}
                            >
                              <div style={documentNameStyles}>
                                <strong>Document Name:</strong>{" "}
                                {isRenaming ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "10px",
                                      marginTop: "5px",
                                    }}
                                  >
                                    <input
                                      type="text"
                                      value={this.state.renameValue}
                                      onChange={(e) =>
                                        this.setState({
                                          renameValue: e.target.value,
                                        })
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          this.saveRename(documentId, displayName);
                                        } else if (e.key === "Escape") {
                                          this.cancelRename();
                                        }
                                      }}
                                      style={{
                                        flex: 1,
                                        padding: "5px 10px",
                                        border: "1px solid #ddd",
                                        borderRadius: "4px",
                                        fontSize: "14px",
                                      }}
                                      autoFocus
                                    />
                                    <button
                                      onClick={() =>
                                        this.saveRename(documentId, displayName)
                                      }
                                      style={{
                                        padding: "5px 15px",
                                        backgroundColor: "#28a745",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                        fontSize: "12px",
                                      }}
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={this.cancelRename}
                                      style={{
                                        padding: "5px 15px",
                                        backgroundColor: "#6c757d",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                        fontSize: "12px",
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "8px",
                                    }}
                                  >
                                    {displayName}
                                    <button
                                      onClick={() =>
                                        this.startRename(documentId, displayName)
                                      }
                                      style={{
                                        padding: "4px 8px",
                                        backgroundColor: "transparent",
                                        border: "1px solid #007bff",
                                        color: "#007bff",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                        fontSize: "11px",
                                        marginLeft: "5px",
                                      }}
                                      title="Rename document"
                                    >
                                      <box-icon
                                        name="edit"
                                        size="14px"
                                        color="#007bff"
                                      ></box-icon>
                                    </button>
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: "12px", color: "#666" }}>
                                Uploaded:{" "}
                                {doc.created_at
                                  ? new Date(doc.created_at).toLocaleDateString()
                                  : "N/A"}
                              </div>
                              <div style={buttonGroupStyles}>
                                {isDownloadOnly ? (
                                  <button
                                    className="submit-button"
                                    onClick={() =>
                                      this.download_pdf(documentId, displayName)
                                    }
                                    style={documentActionButtonStyles}
                                  >
                                    <span>Download</span>
                                    <box-icon
                                      name="download"
                                      color="#fff"
                                      size="14px"
                                      style={documentActionIconStyles}
                                    ></box-icon>
                                  </button>
                                ) : (
                                  <button
                                    className="submit-button"
                                    onClick={() =>
                                      this.view_pdf(documentId, displayName)
                                    }
                                    style={documentActionButtonStyles}
                                  >
                                    <span>View File</span>
                                    <box-icon
                                      name="chevron-right"
                                      color="#fff"
                                      size="14px"
                                      style={documentActionIconStyles}
                                    ></box-icon>
                                  </button>
                                )}
                                <button
                                  className="submit-button"
                                  onClick={() => this.remove_pdf(documentId)}
                                  style={documentActionButtonStyles}
                                >
                                  <span>Remove</span>
                                  <box-icon
                                    name="trash"
                                    color="#fff"
                                    size="14px"
                                    style={documentActionIconStyles}
                                  ></box-icon>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </Spin>
              </div>
            </div>
          </div>
        </div>
        <Footer />

        {this.state.viewingDocument && this.state.documentUrl && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              zIndex: 9999,
              display: "flex",
              flexDirection: "column",
              padding: "20px",
            }}
            onClick={this.closeDocumentViewer}
          >
            <div
              style={{
                position: "absolute",
                top: "20px",
                right: "20px",
                zIndex: 10000,
              }}
            >
              <button
                onClick={this.closeDocumentViewer}
                style={{
                  backgroundColor: "#fff",
                  border: "none",
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                  fontSize: "20px",
                  color: "#333",
                }}
                title="Close"
              >
                ×
              </button>
            </div>
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems:
                  this.state.documentType === "image" ? "flex-start" : "center",
                justifyContent: "center",
                margin: "20px",
                minHeight: 0,
                overflow: "hidden",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {this.state.documentType === "image" ? (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    overflow: "auto",
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      padding: "20px",
                      minHeight: "min-content",
                    }}
                  >
                    <img
                      src={this.state.documentUrl}
                      alt="Document"
                      style={{
                        maxWidth: "100%",
                        width: "auto",
                        height: "auto",
                        display: "block",
                        borderRadius: "8px",
                      }}
                    />
                  </div>
                </div>
              ) : this.state.documentType === "pdf" ? (
                <iframe
                  src={this.state.documentUrl + "#toolbar=0"}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                    borderRadius: "8px",
                    backgroundColor: "#fff",
                  }}
                  title="PDF Viewer"
                />
              ) : (
                <iframe
                  src={this.state.documentUrl}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                    borderRadius: "8px",
                    backgroundColor: "#fff",
                  }}
                  title="Document Viewer"
                />
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}
